import React, { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { Button } from "~/components/ui/button";
import { CopyButton } from "~/components/ui/copy-button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Switch } from "~/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  TroManagementOverviewDocument,
  useCreateTroFromUnknownMutation,
  useTroManagementOverviewQuery,
  useUpdateTroMutation,
} from "generated/hooks";
import { Routes } from "~/routes";
import Loading from "@components/loading/Loading";
import { parseServerError } from "~/util/parseError";

const formatCount = (value: number) => new Intl.NumberFormat().format(value);

const formatLastUsed = (value?: string | null) => {
  if (!value) {
    return "Never";
  }

  return format(new Date(value), "dd/MM/yyyy HH:mm");
};

const AUTOSAVE_DELAY_MS = 700;

type NameSaveState = {
  inFlight: boolean;
  pendingName?: string;
};

const TROManagement: React.FC = () => {
  const { data, loading, error, refetch } = useTroManagementOverviewQuery({
    fetchPolicy: "cache-and-network",
  });
  const [updateTro, updateTroResult] = useUpdateTroMutation();
  const [createTroFromUnknown, createTroFromUnknownResult] =
    useCreateTroFromUnknownMutation();
  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({});
  const [savingNameIds, setSavingNameIds] = useState<Record<string, boolean>>(
    {}
  );
  const [unknownNameDrafts, setUnknownNameDrafts] = useState<
    Record<string, string>
  >({});
  const nameAutosaveTimeouts = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});
  const nameSaveStates = useRef<Record<string, NameSaveState>>({});

  const tros = data?.troManagementOverview.tros ?? [];
  const unknownTros = data?.troManagementOverview.unknownTros ?? [];

  useEffect(() => {
    setNameDrafts((current) => {
      const next = { ...current };

      tros.forEach((tro) => {
        if (next[tro.id] === undefined) {
          next[tro.id] = tro.name;
        }
      });

      return next;
    });
  }, [tros]);

  useEffect(() => {
    return () => {
      Object.values(nameAutosaveTimeouts.current).forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, []);

  const saveName = async (
    id: string,
    currentName: string,
    nextName: string
  ) => {
    if (nextName === currentName) {
      return;
    }

    if (!nextName) {
      toast.error("TRO name is required.");
      return;
    }

    const saveState = (nameSaveStates.current[id] ??= { inFlight: false });
    if (saveState.inFlight) {
      saveState.pendingName = nextName;
      return;
    }

    saveState.inFlight = true;
    setSavingNameIds((current) => ({ ...current, [id]: true }));
    let savedName: string | undefined;
    let pendingName: string | undefined;

    try {
      const result = await updateTro({
        variables: { input: { id, name: nextName } },
        refetchQueries: [TroManagementOverviewDocument],
      });

      const updatedTro = result.data?.updateTRO;

      if (!updatedTro) {
        toast.error("Unable to update TRO name.");
        return;
      }

      savedName = updatedTro.name;
      setNameDrafts((current) => {
        if (current[id] !== undefined && current[id].trim() !== nextName) {
          return current;
        }

        return {
          ...current,
          [id]: updatedTro.name,
        };
      });
      toast.success("TRO name updated.");
      await refetch();
    } catch (mutationError) {
      toast.error(
        `Unable to update TRO name: ${parseServerError(mutationError).message}`
      );
    } finally {
      pendingName = saveState.pendingName;
      delete saveState.pendingName;
      saveState.inFlight = false;

      if (!pendingName || pendingName === (savedName ?? currentName)) {
        delete nameSaveStates.current[id];
        setSavingNameIds((current) => {
          const next = { ...current };
          delete next[id];
          return next;
        });
      }
    }

    if (pendingName && pendingName !== (savedName ?? currentName)) {
      void saveName(id, savedName ?? currentName, pendingName);
    }
  };

  const handleNameChange = (
    id: string,
    currentName: string,
    nextValue: string
  ) => {
    setNameDrafts((current) => ({
      ...current,
      [id]: nextValue,
    }));

    const existingTimeout = nameAutosaveTimeouts.current[id];
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    nameAutosaveTimeouts.current[id] = setTimeout(() => {
      delete nameAutosaveTimeouts.current[id];
      void saveName(id, currentName, nextValue.trim());
    }, AUTOSAVE_DELAY_MS);
  };

  const handleToggleAlerts = async (id: string, showTestAlerts: boolean) => {
    try {
      const result = await updateTro({
        variables: { input: { id, showTestAlerts } },
        refetchQueries: [TroManagementOverviewDocument],
      });

      if (!result.data?.updateTRO) {
        toast.error("Unable to update test alert setting.");
        return;
      }

      toast.success("TRO alert setting updated.");
      await refetch();
    } catch (mutationError) {
      toast.error(
        `Unable to update test alert setting: ${
          parseServerError(mutationError).message
        }`
      );
    }
  };

  const handleCreateUnknown = async (uuid: string) => {
    const name = unknownNameDrafts[uuid]?.trim() ?? "";

    if (!name) {
      toast.error("TRO name is required.");
      return;
    }

    try {
      const result = await createTroFromUnknown({
        variables: { input: { uuid, name } },
        refetchQueries: [TroManagementOverviewDocument],
      });

      if (!result.data?.createTROFromUnknown) {
        toast.error("Unable to create TRO.");
        return;
      }

      setUnknownNameDrafts((current) => {
        const next = { ...current };
        delete next[uuid];
        return next;
      });
      toast.success("TRO created.");
      await refetch();
    } catch (mutationError) {
      toast.error(
        `Unable to create TRO: ${parseServerError(mutationError).message}`
      );
    }
  };

  const mutationLoading =
    updateTroResult.loading || createTroFromUnknownResult.loading;

  return (
    <PageTemplate
      title="TROs"
      subtitle="Test request organisations"
      activeItem="Admin"
      activeSubItem="TROs"
      breadcrumbs={[
        { label: "Admin", path: Routes.Admin },
        { label: "TROs", path: Routes.AdminTROs },
      ]}
    >
      {loading && !data && <Loading />}
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          Error loading TROs: {error.message}
        </div>
      )}

      <Card className="w-full gap-3 py-4">
        <CardHeader className="pb-2">
          <CardTitle>Known TROs</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>UUID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Total tests</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Show test alerts</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tros.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-6 text-center text-muted-foreground"
                  >
                    No TROs found.
                  </TableCell>
                </TableRow>
              ) : (
                tros.map((tro) => {
                  const draft = nameDrafts[tro.id] ?? tro.name;
                  const isSavingName = savingNameIds[tro.id] === true;

                  return (
                    <TableRow key={tro.id}>
                      <TableCell className="font-mono text-xs">
                        <div className="flex items-center gap-1">
                          <span>{tro.uuid}</span>
                          <CopyButton value={tro.uuid} label="TRO UUID" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex min-w-[260px] items-center gap-2">
                          <Input
                            aria-label={`Name for ${tro.uuid}`}
                            value={draft}
                            onChange={(event) =>
                              handleNameChange(
                                tro.id,
                                tro.name,
                                event.target.value
                              )
                            }
                            className="max-w-[320px]"
                          />
                          {isSavingName && (
                            <Loader2
                              className="size-4 animate-spin text-muted-foreground"
                              aria-label={`Saving name for ${tro.uuid}`}
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCount(tro.totalTestCount)}
                      </TableCell>
                      <TableCell>{formatLastUsed(tro.lastUsedAt)}</TableCell>
                      <TableCell>
                        <Switch
                          checked={tro.showTestAlerts}
                          disabled={mutationLoading}
                          onCheckedChange={(checked) =>
                            handleToggleAlerts(tro.id, checked)
                          }
                          aria-label={`Show test alerts for ${tro.name}`}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="w-full gap-3 py-4">
        <CardHeader className="pb-2">
          <CardTitle>Unknown TROs</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>UUID</TableHead>
                <TableHead className="text-right">Total tests</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Name</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {unknownTros.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="py-6 text-center text-muted-foreground"
                  >
                    No unknown TROs found.
                  </TableCell>
                </TableRow>
              ) : (
                unknownTros.map((tro) => {
                  const draft = unknownNameDrafts[tro.uuid] ?? "";
                  const canCreate = draft.trim().length > 0;

                  return (
                    <TableRow key={tro.uuid}>
                      <TableCell className="font-mono text-xs">
                        <div className="flex items-center gap-1">
                          <span>{tro.uuid}</span>
                          <CopyButton value={tro.uuid} label="TRO UUID" />
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCount(tro.totalTestCount)}
                      </TableCell>
                      <TableCell>{formatLastUsed(tro.lastUsedAt)}</TableCell>
                      <TableCell>
                        <div className="flex min-w-[260px] items-center gap-2">
                          <Input
                            aria-label={`Name for unknown ${tro.uuid}`}
                            value={draft}
                            onChange={(event) =>
                              setUnknownNameDrafts((current) => ({
                                ...current,
                                [tro.uuid]: event.target.value,
                              }))
                            }
                            className="max-w-[320px]"
                          />
                          <Button
                            type="button"
                            size="icon"
                            disabled={!canCreate || mutationLoading}
                            onClick={() => handleCreateUnknown(tro.uuid)}
                            aria-label={`Create TRO for ${tro.uuid}`}
                          >
                            <Plus className="size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </PageTemplate>
  );
};

export default TROManagement;
